import { apiFetch } from "@/lib/api";
import type { MrpSkuPlanningMode } from "@/lib/mrp";

export type SgsInventoryDiscrepancyStatus =
  | "pending"
  | "investigating"
  | "resolved_adjustment"
  | "resolved_wastage"
  | "resolved_other"
  | "resolved_prototype"
  | "rejected";

export type SgsInventoryDiscrepancyResolutionKind =
  | "adjustment"
  | "wastage"
  | "prototype"
  | "reject"
  | "other";

export interface InventoryDiscrepancyListRow {
  id: string;
  sku_id: string;
  quantity: number;
  inventory_carton_adjustment_request_id?: string | null;
  carton_box_hex?: string | null;
  carton_short_code?: string | null;
  carton_quantity_before?: number | null;
  carton_quantity_after?: number | null;
  reason: string | null;
  status: SgsInventoryDiscrepancyStatus;
  pending_feedback: boolean;
  submitted_at: string;
  updated_at: string;
  resolution_kind: string | null;
  resolution_note: string | null;
  resolved_at: string | null;
  mv_document_id: string | null;
  mv_document_no: string | null;
  location_id: number;
  submitted_by_display_name: string | null;
  assigned_to_user_id: string | null;
  assigned_to_display_name: string | null;
}

export interface InventoryDiscrepancyMessage {
  id: string;
  body: string;
  is_staff: boolean;
  created_at: string;
  author_display_name: string | null;
}

export interface InventoryDiscrepancySkuContextRow {
  kind: string;
  occurred_at: string | null;
  reference: string | null;
  quantity: number | null;
  /** Sealed quantity on hub transfer lines (sum of pack-list allocations). */
  packed_quantity?: number | null;
  detail: string | null;
  entity_id: string | null;
  mv_entity_id: string | null;
  entity_ref: string | null;
  /** Dock crate for an inbound receipt. Null for every other kind. */
  arrival_package_id: string | null;
  arrival_package_no: string | null;
}

export interface InventoryDiscrepancyRequestDetail {
  request: InventoryDiscrepancyListRow & {
    submitted_by_user_id: string;
    resolved_by_user_id: string | null;
    resolved_by_display_name: string | null;
  };
  messages: InventoryDiscrepancyMessage[];
}

/** @deprecated Use {@link InventoryDiscrepancyRequestDetail} */
export type InventoryDiscrepancyDetail = InventoryDiscrepancyRequestDetail;

export interface InventoryDiscrepancySkuContext {
  sku_id: string;
  sgs_on_hand: number | null;
  planning_mode: MrpSkuPlanningMode | null;
  rows: InventoryDiscrepancySkuContextRow[];
}

export const INVENTORY_DISCREPANCY_QUERY_KEY = ["manufacturing", "inventory-discrepancy"] as const;

export async function fetchInventoryDiscrepancyRequests(input: {
  status?: string;
  pendingFeedback?: boolean;
  sku?: string;
  limit?: number;
  offset?: number;
}): Promise<{ rows: InventoryDiscrepancyListRow[] }> {
  const sku = input.sku?.trim() ?? "";
  return apiFetch<{ rows: InventoryDiscrepancyListRow[] }>(
    "/api/manufacturing/inventory-discrepancy/requests",
    {
      query: {
        ...(input.status != null ? { status: input.status } : {}),
        ...(input.pendingFeedback ? { pending_feedback: "1" } : {}),
        ...(sku !== "" ? { sku } : {}),
        ...(input.limit != null ? { limit: String(input.limit) } : {}),
        ...(input.offset != null ? { offset: String(input.offset) } : {}),
      },
    },
  );
}

export async function fetchInventoryDiscrepancyRequest(
  id: string,
): Promise<InventoryDiscrepancyRequestDetail> {
  return apiFetch<InventoryDiscrepancyRequestDetail>(
    `/api/manufacturing/inventory-discrepancy/requests/${id}`,
  );
}

export async function fetchInventoryDiscrepancySkuContext(input: {
  skuId: string;
  locationId: number;
  limit?: number;
}): Promise<InventoryDiscrepancySkuContext> {
  return apiFetch<InventoryDiscrepancySkuContext>(
    "/api/manufacturing/inventory-discrepancy/sku-context",
    {
      query: {
        sku: input.skuId,
        location_id: String(input.locationId),
        ...(input.limit != null ? { limit: String(input.limit) } : {}),
      },
    },
  );
}

export async function createInventoryDiscrepancyRequest(body: {
  sku_id: string;
  quantity: number;
  reason?: string | null;
  transfer_order_id?: number | null;
  transfer_order_item_id?: number | null;
}): Promise<{ request: { id: string } }> {
  return apiFetch<{ request: { id: string } }>(
    "/api/manufacturing/inventory-discrepancy/requests",
    { method: "POST", body },
  );
}

export async function patchInventoryDiscrepancyRequest(
  id: string,
  body: {
    status?: SgsInventoryDiscrepancyStatus;
    pending_feedback?: boolean;
    assigned_to_user_id?: string | null;
    resolution_kind?: SgsInventoryDiscrepancyResolutionKind;
    resolution_note?: string | null;
  },
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/api/manufacturing/inventory-discrepancy/requests/${id}`, {
    method: "PATCH",
    body,
  });
}

export async function postInventoryDiscrepancyMessage(
  id: string,
  body: string,
  staff: boolean,
): Promise<{ ok: boolean }> {
  const path = staff
    ? `/api/manufacturing/inventory-discrepancy/requests/${id}/staff-messages`
    : `/api/manufacturing/inventory-discrepancy/requests/${id}/messages`;
  return apiFetch<{ ok: boolean }>(path, { method: "POST", body: { body } });
}

export interface InventoryDiscrepancySettings {
  notifyUserIds: string[];
  updatedAt: string | null;
}

export const INVENTORY_DISCREPANCY_SETTINGS_QUERY_KEY = [
  "manufacturing",
  "inventory-discrepancy",
  "settings",
] as const;

export const INVENTORY_DISCREPANCY_ASSIGNEES_QUERY_KEY = [
  "manufacturing",
  "inventory-discrepancy",
  "assignees",
] as const;

export const OPEN_INVENTORY_DISCREPANCY_SKUS_QUERY_KEY = [
  "manufacturing",
  "inventory-discrepancy",
  "open-skus",
] as const;

export async function fetchOpenInventoryDiscrepancySkus(skus: string[]): Promise<{ open_skus: string[] }> {
  const unique = [...new Set(skus.map((sku) => sku.trim()).filter((sku) => sku.length > 0))];
  if (unique.length === 0) {
    return { open_skus: [] };
  }
  return apiFetch<{ open_skus: string[] }>("/api/manufacturing/inventory-discrepancy/open-skus", {
    query: { skus: unique.join(",") },
  });
}

export interface InventoryDiscrepancyAssignee {
  id: string;
  display_name: string;
}

export async function fetchInventoryDiscrepancyAssignees(): Promise<{
  users: InventoryDiscrepancyAssignee[];
}> {
  return apiFetch<{ users: InventoryDiscrepancyAssignee[] }>(
    "/api/manufacturing/inventory-discrepancy/assignees",
  );
}

export async function fetchInventoryDiscrepancySettings(): Promise<InventoryDiscrepancySettings> {
  return apiFetch<InventoryDiscrepancySettings>(
    "/api/manufacturing/inventory-discrepancy/settings",
  );
}

export async function patchInventoryDiscrepancySettings(
  notifyUserIds: string[],
): Promise<InventoryDiscrepancySettings> {
  return apiFetch<InventoryDiscrepancySettings>(
    "/api/manufacturing/inventory-discrepancy/settings",
    { method: "PATCH", body: { notifyUserIds } },
  );
}

export function inventoryDiscrepancyStatusLabel(status: SgsInventoryDiscrepancyStatus): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "investigating":
      return "Investigating";
    case "resolved_adjustment":
      return "Approved (adjustment)";
    case "resolved_wastage":
      return "Approved (wastage)";
    case "resolved_prototype":
      return "Approved (prototype)";
    case "resolved_other":
      return "Approved (other)";
    case "rejected":
      return "Rejected";
    default:
      return status;
  }
}
